// qs-brush-Dashed.ts
import type { KonvaLine } from '@/types/qs-type-konva'
import Konva from 'konva'
import { BaseBrush } from '../base/qs-brushes-base'
import type { BrushConfig, BrushContext, BrushDrawingResult, BrushProperties, BrushRecreationContext } from '../qs-brushes-types'

export class DashedBrush extends BaseBrush {
  readonly config: BrushConfig = {
    id: 'dashed',
    name: 'Dashed Brush',
    category: 'basic',
    description: 'Dashed line brush',
    icon: '┅',
    minSize: 1,
    maxSize: 100,
    defaultSize: 5
  }

  getProperties(size: number): BrushProperties {
    return {
      lineCap: 'round',
      lineJoin: 'round',
      dashPattern: [size * 3, size * 2],
      strokeWidth: size
    }
  }

  createBrushLine(context: BrushContext): KonvaLine {
    const properties = this.createBaseLineProperties(context)

    return new Konva.Line({
      id: `path-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      points: [context.position.x, context.position.y],
      ...properties,
      dash: [...properties.dashPattern]
    })
  }

  continueDrawing(existingLine: KonvaLine, context: BrushContext): void {
    const currentPoints = existingLine.points()
    const newPoints = currentPoints.concat([context.position.x, context.position.y])
    existingLine.points(newPoints)
  }

  async finishDrawing(existingLine: KonvaLine, context: BrushContext): Promise<BrushDrawingResult> {
    const points = existingLine.points()
    const properties = this.getProperties(context.size)
    const pathId = existingLine.id()

    const originalPath = this.createOriginalPath(points, context, pathId)

    return {
      konvaObjects: [existingLine],
      originalPath: originalPath,
      pathData: {
        id: pathId,
        layerId: context.layerId,
        points: points,
        stroke: context.color,
        strokeWidth: properties.strokeWidth,
        lineCap: properties.lineCap,
        lineJoin: properties.lineJoin,
        dash: properties.dashPattern || [],
        opacity: context.opacity,
        timestamp: Date.now(),
        brushType: this.config.id,
        isTapered: false,
        isThickToThin: false,
        isThinToThick: false,
        isSpray: false,
        isDiagonal: false,
        segmentCount: 0,
        dotCount: 0,
        visualData: {
          segments: [],
          dots: []
        }
      }
    }
  }

  async recreateFromData(context: BrushRecreationContext): Promise<void> {
    const { pathData, konvaLayer } = context
    const properties = this.getProperties(pathData.strokeWidth)
    const rawPoints = Array.from(pathData.points)
    const smoothed = pathData.smoothedPoints && pathData.smoothedPoints.length >= 4
      ? Array.from(pathData.smoothedPoints)
      : rawPoints

    const konvaLine = new Konva.Line({
      id: pathData.id,
      points: smoothed,
      stroke: pathData.stroke,
      strokeWidth: pathData.strokeWidth,
      lineCap: properties.lineCap,
      lineJoin: properties.lineJoin,
      dash: pathData.dash ? Array.from(pathData.dash) : Array.from(properties.dashPattern),
      opacity: pathData.opacity,
      draggable: false
    })

    konvaLayer.add(konvaLine)

    const originalPath = this.createOriginalPath(rawPoints, {
      position: { x: 0, y: 0 },
      size: pathData.strokeWidth,
      color: pathData.stroke,
      opacity: pathData.opacity,
      layerId: pathData.id
    }, pathData.id)

    konvaLayer.add(originalPath)
  }
}